import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlined";
import { Box, Card, CardActionArea, CardContent, Chip, Typography } from "@mui/material";
import type { FC } from "react";
import type { SurveyQuestion } from "./staffSatisfactionTypes";

interface QuestionCardProps {
	question: SurveyQuestion;
	responseCount: number;
	onClick: () => void;
}

export const QuestionCard: FC<QuestionCardProps> = ({ question, responseCount, onClick }) => {
	let scoreColor = "success.main";
	let scoreBg = "#edf7ed";
	if (question.score < 3) {
		scoreColor = "error.main";
		scoreBg = "#fdeded";
	} else if (question.score < 4) {
		scoreColor = "warning.main";
		scoreBg = "#fff4e5";
	}

	return (
		<Card
			elevation={0}
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 2,
				transition: "all 0.2s ease-in-out",
				"&:hover": {
					borderColor: "primary.main",
					boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
					transform: "translateY(-2px)",
				},
			}}
		>
			<CardActionArea
				onClick={onClick}
				sx={{
					height: "100%",
					display: "flex",
					flexDirection: "column",
					alignItems: "stretch",
					justifyContent: "flex-start",
				}}
			>
				<CardContent
					sx={{
						flexGrow: 1,
						display: "flex",
						flexDirection: "column",
						p: 3,
					}}
				>
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, gap: 2 }}>
						<Typography
							variant="h6"
							component="h2"
							sx={{
								fontWeight: 600,
								lineHeight: 1.3,
								flexGrow: 1,
							}}
						>
							{question.text}
						</Typography>
						<Box
							sx={{
								bgcolor: scoreBg,
								color: scoreColor,
								px: 1.5,
								py: 0.5,
								borderRadius: 1,
								fontWeight: "bold",
								fontSize: "1.1rem",
								display: "flex",
								alignItems: "center",
								flexShrink: 0,
							}}
						>
							{question.score.toFixed(1)}
							<Typography variant="caption" sx={{ ml: 0.5, color: "text.secondary", fontWeight: "normal" }}>
								/ 5
							</Typography>
						</Box>
					</Box>

					<Box
						sx={{
							bgcolor: "primary.50",
							p: 2.5,
							borderRadius: 2,
							display: "flex",
							flexDirection: "column",
							flexGrow: 1,
							mt: 1,
						}}
					>
						<Typography
							variant="body1"
							sx={{
								color: "text.primary",
								lineHeight: 1.6,
								fontSize: "1.05rem",
								mb: 2,
							}}
						>
							{question.aiSummary}
						</Typography>

						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: "auto" }}>
							<Chip
								icon={<ChatBubbleOutlineIcon sx={{ fontSize: "1rem !important" }} />}
								label={`${responseCount} responses`}
								size="small"
								variant="outlined"
								sx={{ fontWeight: 500, bgcolor: "white" }}
							/>
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
								<Typography
									variant="caption"
									sx={{
										color: "primary.main",
										fontWeight: 600,
										fontSize: "0.75rem",
										textTransform: "uppercase",
										letterSpacing: "0.5px",
										opacity: 0.7,
									}}
								>
									{"AI Summary"}
								</Typography>
								<AutoAwesomeIcon sx={{ color: "primary.main", fontSize: "0.9rem", opacity: 0.7 }} />
							</Box>
						</Box>
					</Box>
				</CardContent>
			</CardActionArea>
		</Card>
	);
};
